import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAllAnalyticsData, type AllAnalyticsData } from '@/service';

interface AnalyticsContextValue {
  data: AllAnalyticsData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

interface AnalyticsProviderProps {
  children: React.ReactNode;
  autoFetch?: boolean; // 是否自动获取数据，默认 true
  refetchInterval?: number; // 自动刷新间隔（毫秒），默认不自动刷新
}

export function AnalyticsProvider({
  children,
  autoFetch = true,
  refetchInterval,
}: AnalyticsProviderProps) {
  const [data, setData] = useState<AllAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchAllAnalyticsData();
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch analytics data');
      setError(error);
      console.error('Failed to fetch analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化时获取数据
  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchData]);

  // 自动刷新
  useEffect(() => {
    if (!refetchInterval || refetchInterval <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchData();
    }, refetchInterval);

    return () => clearInterval(intervalId);
  }, [refetchInterval, fetchData]);

  const value: AnalyticsContextValue = {
    data,
    loading,
    error,
    refetch: fetchData,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

/**
 * 使用 Analytics 数据的 Hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useAnalytics();
 *
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Total Users: {data?.overview?.data.totalUsers}</h2>
 *       <button onClick={refetch}>Refresh</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);

  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return context;
}
